/* File: src/contexts/GameContext.js */
import React, {
    createContext,
    useState,
    useContext,
    useEffect,
    useRef,
    useCallback,
    useMemo
} from 'react';
import { useProfile } from './ProfileContext';
import { useSettings } from './SettingsContext';
import audioManager from '../utils/audioManager';
import { createGameActions } from './game/gameActions';
import { createGameSetupActions } from './game/gameSetup';
import { createEndGameAction } from './game/gameEnd'; // <<< endGameLogic is already async
import { createChallengeActions } from './game/gameChallenge';
import { createGameGetters } from './game/gameGetters';
import { createLogReplayEvent } from './game/replayUtils';

export const GameContext = createContext(null);

export const useGame = () => {
    const context = useContext(GameContext);
    if (!context) { throw new Error('useGame must be used within a GameProvider'); }
    return context;
};

export const GameProvider = ({ children }) => {
    // --- State Definitions ---
    const [gameState, setGameState] = useState('idle');
    const [pauseReason, setPauseReason] = useState(null);
    const [nBack, setNBack] = useState(2);
    const [difficulty, setDifficulty] = useState(0.0);
    const [sequence, setSequence] = useState([]);
    const [currentTrial, setCurrentTrial] = useState(-1);
    const [totalTrials, setTotalTrials] = useState(0);
    const [gameResults, setGameResults] = useState(null);
    const [feedbackMessage, setFeedbackMessage] = useState('');
    const [consecutiveSuccesses, setConsecutiveSuccesses] = useState(0);
    const [showCountdown, setShowCountdown] = useState(false);
    const [saveToProfile, setSaveToProfile] = useState(true);
    const [isPlaygroundSession, setIsPlaygroundSession] = useState(false);
    const [playgroundSettings, setPlaygroundSettings] = useState(null);
    const [challengeState, setChallengeState] = useState({ isActive: false, targetNBack: null, wins: 0 });
    const [isCountdownVisuallyComplete, setIsCountdownVisuallyComplete] = useState(false);

    // --- Refs ---
    const visualMatchesRef = useRef([]);
    const audioMatchesRef = useRef([]);
    const visualResponsesRef = useRef([]);
    const audioResponsesRef = useRef([]);
    const timerRef = useRef(null);
    const isEndingRef = useRef(false);
    const sessionSettingsRef = useRef(null);
    const replayEventsRef = useRef([]);
    const sessionStartTimeRef = useRef(null);
    const onNavigateRef = useRef(null);
    const latestGameStateRef = useRef(gameState);
    const currentTrialRef = useRef(currentTrial);
    const totalTrialsRef = useRef(totalTrials);
    const nBackRef = useRef(nBack);
    const difficultyRef = useRef(difficulty);
    const consecutiveSuccessesRef = useRef(consecutiveSuccesses);
    const audioTriggeredForTrial = useRef(-1);

    // --- External Contexts ---
    const { activeProfile, updateProfileProgress } = useProfile();
    const { settings } = useSettings();

    // --- Initialization Effects ---
    useEffect(() => { audioManager.init(); }, []);

    // --- Ref Sync Effects ---
    useEffect(() => { latestGameStateRef.current = gameState; }, [gameState]);
    useEffect(() => { currentTrialRef.current = currentTrial; }, [currentTrial]);
    useEffect(() => { totalTrialsRef.current = totalTrials; }, [totalTrials]);
    useEffect(() => { nBackRef.current = nBack; }, [nBack]);
    useEffect(() => { difficultyRef.current = difficulty; }, [difficulty]);
    useEffect(() => { consecutiveSuccessesRef.current = consecutiveSuccesses; }, [consecutiveSuccesses]);

    // --- Replay Logging Action ---
    const logReplayEvent = useCallback(createLogReplayEvent(replayEventsRef), [replayEventsRef]);

    // --- Memoized Action Logics (Created via Factory Functions) ---
    const {
        pauseGameLogic, resumeGameLogic, clearPauseReasonLogic,
        playAfterCountdownLogic, registerResponseLogic,
        setManualSettingsLogic, resetContextResponseArraysLogic
    } = useMemo(() => createGameActions({
        setGameState, setNBack, setDifficulty, setSaveToProfile, setCurrentTrial,
        setShowCountdown, setIsCountdownVisuallyComplete, setPauseReason,
        latestGameStateRef, visualResponsesRef, audioResponsesRef, logReplayEvent,
        timerRef, sessionStartTimeRef, isEndingRef, audioManager
    }), [
        logReplayEvent, setGameState, setNBack, setDifficulty, setSaveToProfile,
        setCurrentTrial, setShowCountdown, setIsCountdownVisuallyComplete, setPauseReason
    ]);

    const startCountdown = useCallback(() => {
         audioManager.resume().then(resumed => {
            if (resumed) {
                setGameState('preparing'); setPauseReason(null); setFeedbackMessage('');
                setShowCountdown(true); setIsCountdownVisuallyComplete(false);
                audioTriggeredForTrial.current = -1;
                const isPlayground = isPlaygroundSession;
                const n = isPlayground ? playgroundSettings?.nBack : activeProfile?.currentNBack;
                const d = isPlayground ? playgroundSettings?.difficulty : activeProfile?.currentDifficulty;
                const voice = settings?.selectedVoiceId;
                const actualN = n ?? 2; const actualD = d ?? 0.0;
                const difficultySeedForPreload = Math.max(0, Math.min(1.0, actualD));
                if (voice) { audioManager.preload({ difficulties: [difficultySeedForPreload], voiceId: voice }); }
                else { console.warn("[GameContext->startCountdown] Cannot preload audio: No voice selected."); }
            } else {
                 console.error("[GameContext->startCountdown] Failed to resume audio context.");
                 setFeedbackMessage("Audio could not be started. Please click anywhere on the page or try again.");
            }
        });
    }, [
        setGameState, setPauseReason, setFeedbackMessage, setShowCountdown, setIsCountdownVisuallyComplete,
        isPlaygroundSession, playgroundSettings, activeProfile, settings?.selectedVoiceId
    ]);

    const { startGameLogic, startPlaygroundGameLogic, cancelPreparationLogic } = useMemo(() => createGameSetupActions({
        activeProfile, saveToProfileState: saveToProfile, challengeState, isPlaygroundSessionState: isPlaygroundSession,
        setGameState, setNBack, setDifficulty, setSequence, setCurrentTrial,
        setTotalTrials, setGameResults, setFeedbackMessage, setIsPlaygroundSession,
        setSaveToProfile, setPlaygroundSettings, startCountdown, setChallengeState,
        setConsecutiveSuccesses, setShowCountdown, setIsCountdownVisuallyComplete, setPauseReason,
        timerRef, isEndingRef, visualResponsesRef, audioResponsesRef, visualMatchesRef,
        audioMatchesRef, sessionSettingsRef, replayEventsRef, sessionStartTimeRef,
        onNavigateRef, latestGameStateRef, audioManager
    }), [
        activeProfile, saveToProfile, challengeState, isPlaygroundSession, startCountdown,
        setGameState, setNBack, setDifficulty, setSequence, setCurrentTrial,
        setTotalTrials, setGameResults, setFeedbackMessage, setIsPlaygroundSession,
        setSaveToProfile, setPlaygroundSettings, setChallengeState,
        setConsecutiveSuccesses, setShowCountdown, setIsCountdownVisuallyComplete, setPauseReason
    ]);

    const { enterChallengeLogic, exitChallengeLogic, completeChallengeLogic } = useMemo(() => createChallengeActions({
        activeProfile, updateProfileProgress, challengeState, setChallengeState, setGameResults, setGameState,
        setNBack, setDifficulty, setConsecutiveSuccesses, setFeedbackMessage, onNavigateRef, setPauseReason
    }), [
        activeProfile, updateProfileProgress, challengeState,
        setChallengeState, setGameResults, setGameState, setNBack, setDifficulty,
        setConsecutiveSuccesses, setFeedbackMessage, setPauseReason
    ]);

    const endGameLogic = useMemo(() => createEndGameAction({
        activeProfile, isPlaygroundSession, saveToProfile, challengeState, setChallengeState, logReplayEvent,
        setGameState, setGameResults, setFeedbackMessage, setIsPlaygroundSession, setPlaygroundSettings,
        setCurrentTrial, setSequence, setTotalTrials, setNBack, setDifficulty, setConsecutiveSuccesses,
        setSaveToProfile, updateProfileProgress, completeChallengeLogic, setPauseReason,
        latestGameStateRef, nBackRef, difficultyRef, consecutiveSuccessesRef, currentTrialRef, totalTrialsRef,
        onNavigateRef, isEndingRef, visualResponsesRef, audioResponsesRef,
        sessionSettingsRef, sessionStartTimeRef, replayEventsRef
    }), [
        activeProfile, isPlaygroundSession, saveToProfile, challengeState, logReplayEvent,
        updateProfileProgress, completeChallengeLogic,
        setChallengeState, setGameState, setGameResults, setFeedbackMessage,
        setIsPlaygroundSession, setPlaygroundSettings, setCurrentTrial, setSequence,
        setTotalTrials, setNBack, setDifficulty, setConsecutiveSuccesses, setSaveToProfile,
        setPauseReason
    ]);

    const {
        isMatchLogic, getStimulusDurationLogic, getInstantFeedbackEnabledLogic, getCoachingMessageLogic,
        getFeatureIntroMessageLogic, getVisualMatchesLogic, getAudioMatchesLogic, getUserVisualResponsesLogic,
        getUserAudioResponsesLogic, getPlaygroundSettingLogic
    } = useMemo(() => createGameGetters({
        sequence, nBackRef, difficultyRef, isPlaygroundSession, playgroundSettings, sessionSettingsRef,
        visualMatchesRef, audioMatchesRef, visualResponsesRef, audioResponsesRef, settings
    }), [
        sequence, isPlaygroundSession, playgroundSettings, settings
    ]);


    // --- Stable Action Callbacks (Exported via Context Value) ---
    const pauseGame = useCallback((reason = 'manual') => { pauseGameLogic(reason); }, [pauseGameLogic]);
    const resumeGame = useCallback(() => {
        audioManager.resume().then(resumed => {
            if(resumed) { resumeGameLogic(); }
            else {
                console.error("[GameContext->resumeGame] Failed to resume audio.");
                setFeedbackMessage("Could not resume audio. Please interact with the page and try again.");
                setGameState('paused');
            }
        });
    }, [resumeGameLogic, setFeedbackMessage, setGameState]);

    const clearPauseReason = useCallback(() => { clearPauseReasonLogic(); }, [clearPauseReasonLogic]);
    const playAfterCountdown = useCallback(playAfterCountdownLogic, [playAfterCountdownLogic]);
    const registerResponse = useCallback(registerResponseLogic, [registerResponseLogic]);
    const setManualSettings = useCallback(setManualSettingsLogic, [setManualSettingsLogic]);
    const resetContextResponseArrays = useCallback(resetContextResponseArraysLogic, [resetContextResponseArraysLogic]);
    const setNavigationHandler = useCallback((handler) => { onNavigateRef.current = handler; }, []);
    const startGame = useCallback(startGameLogic, [startGameLogic]);
    const startPlaygroundGame = useCallback(startPlaygroundGameLogic, [startPlaygroundGameLogic]);
    const cancelPreparation = useCallback(cancelPreparationLogic, [cancelPreparationLogic]);
    const enterChallenge = useCallback(enterChallengeLogic, [enterChallengeLogic]);
    const exitChallenge = useCallback(exitChallengeLogic, [exitChallengeLogic]);
    const completeChallenge = useCallback(completeChallengeLogic, [completeChallengeLogic]);

    // *** MODIFIED: endGame wrapper is now async and awaits the logic ***
    // This allows GameScreen to await the completion of result processing.
    const endGame = useCallback(async (options = { skipResultsProcessing: false }) => {
        // endGameLogic itself is already async
        await endGameLogic(options); // <<< await the async logic function
    }, [endGameLogic]); // Dependency is the memoized logic function
    // *** END MODIFIED CODE ***

    // --- Stable Getter Callbacks (Exported via Context Value) ---
    const isMatch = useCallback(isMatchLogic, [isMatchLogic]);
    const getStimulusDuration = useCallback(getStimulusDurationLogic, [getStimulusDurationLogic]);
    const getInstantFeedbackEnabled = useCallback(getInstantFeedbackEnabledLogic, [getInstantFeedbackEnabledLogic]);
    const getCoachingMessage = useCallback(getCoachingMessageLogic, [getCoachingMessageLogic]);
    const getFeatureIntroMessage = useCallback(getFeatureIntroMessageLogic, [getFeatureIntroMessageLogic]);
    const getVisualMatches = useCallback(getVisualMatchesLogic, [getVisualMatchesLogic]);
    const getAudioMatches = useCallback(getAudioMatchesLogic, [getAudioMatchesLogic]);
    const getUserVisualResponses = useCallback(getUserVisualResponsesLogic, [getUserVisualResponsesLogic]);
    const getUserAudioResponses = useCallback(getUserAudioResponsesLogic, [getUserAudioResponsesLogic]);
    const getPlaygroundSetting = useCallback(getPlaygroundSettingLogic, [getPlaygroundSettingLogic]);
    const playFeedbackSound = useCallback((type) => audioManager.playFeedbackSound(type), []);

    // --- Core Game Logic Effects ---
    // (Effects remain the same)
    useEffect(() => { // Countdown completion
        if (isCountdownVisuallyComplete && gameState === 'preparing') {
            console.log("[GameContext Effect] Countdown complete, transitioning to 'playing'.");
            sessionStartTimeRef.current = Date.now();
            logReplayEvent({ type: 'gameStart', timestamp: sessionStartTimeRef.current });
            setCurrentTrial(0);
            logReplayEvent({ type: 'trialStart', trialIndex: 0, timestamp: Date.now() });
            setGameState('playing');
            setPauseReason(null);
            setIsCountdownVisuallyComplete(false);
        }
    }, [isCountdownVisuallyComplete, gameState, logReplayEvent, setCurrentTrial, setGameState, setPauseReason]);

    useEffect(() => { // Audio stimulus playback
        if (gameState === 'playing' && currentTrial >= 0 && currentTrial < sequence.length && currentTrial !== audioTriggeredForTrial.current) {
            const currentStimulus = sequence[currentTrial];
            if (currentStimulus?.letter) {
                const trialToPlay = currentTrial;
                audioTriggeredForTrial.current = trialToPlay;
                queueMicrotask(() => {
                    if (latestGameStateRef.current === 'playing' && currentTrialRef.current === trialToPlay) {
                         const selectedVoiceId = settings?.selectedVoiceId;
                         if (!selectedVoiceId) { console.error(`...No voice selected.`); setFeedbackMessage("Warning: No audio voice selected in settings."); return; }
                         audioManager.playItem(currentStimulus.letter, difficultyRef.current, { voiceId: selectedVoiceId });
                         const playedTimestamp = Date.now();
                         // --- MODIFICATION START ---
                         logReplayEvent({
                             type: 'audioStimulusStart',
                             trialIndex: trialToPlay,
                             timestamp: playedTimestamp,
                             letter: currentStimulus.letter,
                             voice: selectedVoiceId,
                             difficulty: difficultyRef.current // Log the difficulty at the time of playback
                         });
                         // --- MODIFICATION END ---
                    }
                });
            }
        } else if (gameState !== 'playing' && gameState !== 'paused') { audioManager.stopCurrentSound(); }
    }, [gameState, currentTrial, sequence, settings?.selectedVoiceId, logReplayEvent, setFeedbackMessage]);

    useEffect(() => { // Main game timer
        let intervalId = null;
        if (gameState === 'playing') {
            if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
            const isiMs = (sessionSettingsRef.current?.calculatedISI ?? 2.5) * 1000;
            console.log(`[GameContext Timer Effect] Setting up timer with ISI: ${isiMs}ms.`);
            if (isNaN(isiMs) || isiMs <= 100) { console.error(`...Invalid ISI...`); setGameState('idle'); setFeedbackMessage("Error: Invalid game speed configuration."); return; }
            intervalId = setInterval(() => {
                if (isEndingRef.current || latestGameStateRef.current !== 'playing') { if (intervalId) clearInterval(intervalId); timerRef.current = null; return; }
                setCurrentTrial(prevTrial => {
                    const nextTrial = prevTrial + 1;
                    const totalTrialsFromRef = totalTrialsRef.current;
                    if (nextTrial >= totalTrialsFromRef) {
                        console.log(`...Reached end of sequence... Ending game.`);
                        if (intervalId) clearInterval(intervalId); timerRef.current = null;
                        queueMicrotask(() => endGame()); // Call default endGame
                        return prevTrial;
                    } else {
                        logReplayEvent({ type: 'trialStart', trialIndex: nextTrial, timestamp: Date.now() });
                        return nextTrial;
                    }
                });
            }, isiMs);
            timerRef.current = intervalId;
        } else { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } }
        return () => { if (intervalId) clearInterval(intervalId); if (timerRef.current === intervalId) timerRef.current = null; };
    }, [gameState, endGame, logReplayEvent, setCurrentTrial, setGameState, setFeedbackMessage]);

    useEffect(() => { // Sync from profile when idle
        if (gameState === 'idle' && activeProfile && !challengeState.isActive && !isPlaygroundSession) {
            const profileNBack = activeProfile.currentNBack ?? 2;
            const profileDifficulty = Math.max(0, Math.min(0.95, activeProfile.currentDifficulty ?? 0.0));
            const profileStreak = activeProfile.consecutiveSuccesses ?? 0;
            if (nBack !== profileNBack) { setNBack(profileNBack); }
            if (Math.abs(difficulty - profileDifficulty) > 0.001) { setDifficulty(profileDifficulty); }
            if (consecutiveSuccesses !== profileStreak) { setConsecutiveSuccesses(profileStreak); }
            if (!saveToProfile) { setSaveToProfile(true); }
            if (showCountdown) { setShowCountdown(false); }
            if (pauseReason !== null) { setPauseReason(null); }
        }
    }, [
        activeProfile, gameState, challengeState.isActive, isPlaygroundSession,
        nBack, difficulty, consecutiveSuccesses, saveToProfile, showCountdown, pauseReason,
        setNBack, setDifficulty, setConsecutiveSuccesses, setSaveToProfile, setShowCountdown, setPauseReason
    ]);


    // --- Assemble Context Value ---
    const value = useMemo(() => ({
        // State
        gameState, nBack, difficulty, sequence, currentTrial, totalTrials, gameResults,
        feedbackMessage, consecutiveSuccesses, isPlaygroundSession,
        showCountdown, challengeState, saveToProfile, pauseReason,

        // Actions
        pauseGame, resumeGame, clearPauseReason, playAfterCountdown, registerResponse,
        setManualSettings, startGame, startPlaygroundGame, endGame, // endGame wrapper is now async
        logReplayEvent, cancelPreparation, setSaveToProfile, setNavigationHandler,
        enterChallenge, exitChallenge, completeChallenge, resetContextResponseArrays,

        // Getters
        isMatch, getStimulusDuration, getInstantFeedbackEnabled, getCoachingMessage, getFeatureIntroMessage,
        getVisualMatches, getAudioMatches, getUserVisualResponses, getUserAudioResponses,
        getPlaygroundSetting,

        // Direct Audio Controls
        resumeAudio: audioManager.resume, playFeedbackSound,

        // Refs
        latestGameStateRef, sessionSettingsRef, currentTrialRef, isEndingRef

    }), [ // Include all state and stable callbacks/getters in dependency array
        gameState, nBack, difficulty, sequence, currentTrial, totalTrials, gameResults,
        feedbackMessage, consecutiveSuccesses, isPlaygroundSession, showCountdown,
        challengeState, saveToProfile, pauseReason,
        pauseGame, resumeGame, clearPauseReason, playAfterCountdown, registerResponse,
        setManualSettings, startGame, startPlaygroundGame, endGame, logReplayEvent, cancelPreparation,
        setSaveToProfile, setNavigationHandler, enterChallenge, exitChallenge, completeChallenge,
        resetContextResponseArrays,
        isMatch, getStimulusDuration, getInstantFeedbackEnabled, getCoachingMessage, getFeatureIntroMessage,
        getVisualMatches, getAudioMatches, getUserVisualResponses, getUserAudioResponses,
        getPlaygroundSetting, playFeedbackSound,
        latestGameStateRef, sessionSettingsRef, currentTrialRef, isEndingRef
    ]);

    // --- Render Provider ---
    return (
        <GameContext.Provider value={value}>
            {children}
        </GameContext.Provider>
    );
};